import React, { useEffect, useMemo, useState } from "react";
import { allProperties } from "../actions/allProperties";
import PropColumn from "../components/PropColumn";
import Footer from "../components/Footer";
import { AiOutlineUnorderedList, AiOutlineBars } from "react-icons/ai";
import { BsCardImage } from "react-icons/bs";
import { cloudinaryUrl } from "../constants/CloudinaryConstants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SortDropdown from "../components/SortDropdown";
import { sortByAddress } from "../utils/sortHelpers";

const Expired = () => {
  const [properties, setProperties] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [previewImage, setPreviewImage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const initialQ = searchParams.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [city, setCity] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  useEffect(() => {
    allProperties({ setProperties });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (q) {
      params.set("q", q);
    } else {
      params.delete("q");
    }
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const filtered = useMemo(() => {
    if (!Array.isArray(properties)) return [];

    let result = properties.filter((p) => {
      if (p.property_status !== "Expired") return false;

      const keyword = (q || "").toLowerCase().trim();
      const addr = `${p.street_number || ""} ${p.street_name || ""} ${
        p.city || ""
      } ${p.zip || ""}`.toLowerCase();
      const keyMatch = keyword
        ? String(p.property_id || "")
            .toLowerCase()
            .includes(keyword) || addr.includes(keyword)
        : true;

      const cityMatch = city
        ? (p.city || "").toLowerCase() === city.toLowerCase()
        : true;

      const typeMatch = typeFilter
        ? (p.type || "").toLowerCase() === typeFilter.toLowerCase()
        : true;

      return keyMatch && cityMatch && typeMatch;
    });

    result = sortByAddress(result, sortOrder);

    return result;
  }, [properties, q, city, typeFilter, sortOrder]);

  const clearFilters = () => {
    setQ("");
    setCity("");
    setTypeFilter("");
    setSortOrder("");
  };

  return (
    <>
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* Top Header */}
        <div className="mt-8 mb-6 px-6">
          <h1 className="text-5xl font-bold text-slate-900">
            Expired Properties
          </h1>
          <p className="text-slate-600 mt-2">
            {filtered?.length || 0} properties available
          </p>
        </div>

        <div className="flex gap-6 px-6 pb-6 flex-1">
          {/* Left Sidebar - Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-32 space-y-4">
              {/* View Mode Buttons */}
              <div className="flex gap-2 bg-white rounded-lg p-2 shadow-sm border border-slate-200">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all font-medium text-sm ${
                    viewMode === "cards"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                  }`}
                  title="Card View"
                >
                  <AiOutlineUnorderedList size={18} />
                  <span>Cards</span>
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`flex-1 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all font-medium text-sm ${
                    viewMode === "table"
                      ? "bg-blue-500 text-white"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                  }`}
                  title="Detailed View"
                >
                  <AiOutlineBars size={18} />
                  <span>Table</span>
                </button>
              </div>

              {/* Type Filter - Simplified */}
              <div className="bg-white border border-slate-300 rounded-lg p-3 space-y-3 shadow-sm">
                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Property Type
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTypeFilter("")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        typeFilter === ""
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setTypeFilter("Residential")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        typeFilter === "Residential"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      Residential
                    </button>
                    <button
                      onClick={() => setTypeFilter("Commercial")}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        typeFilter === "Commercial"
                          ? "bg-green-600 text-white"
                          : "bg-slate-100 text-gray-700 hover:bg-slate-200"
                      }`}
                    >
                      Commercial
                    </button>
                  </div>
                </div>
              </div>

              {/* Basic Filters */}
              <div className="bg-white border border-slate-300 rounded-lg p-4 space-y-4 shadow-sm">
                <div>
                  <h3 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">
                    Search & Location
                  </h3>
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        Keyword / Key #
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="12345 or Main"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs font-semibold text-slate-700 mb-1">
                        City
                      </label>
                      <input
                        className="bg-slate-100 px-3 py-2 rounded border border-slate-200 focus:border-blue-500 focus:bg-white text-xs transition-all"
                        type="text"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-slate-200 pt-3 flex gap-2">
                  <button
                    className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-black transition-all font-medium text-xs"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                  <SortDropdown
                    label="Sort"
                    value={sortOrder}
                    onChange={setSortOrder}
                    options={[
                      { value: "", label: "Default" },
                      { value: "a-z", label: "A - Z" },
                      { value: "z-a", label: "Z - A" },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            {viewMode === "cards" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative auto-rows-min items-start justify-center">
                {filtered?.map((property, index) => (
                  <PropColumn
                    key={index}
                    property={property}
                    isFullWidth={true}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto p-6">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-200 sticky top-0">
                    <tr>
                      <th className="border px-4 py-2 text-center">Key#</th>
                      <th className="border px-4 py-2 text-center">Preview</th>
                      <th className="border px-4 py-2 text-left">Address</th>
                      <th className="border px-4 py-2 text-left">City</th>
                      <th className="border px-4 py-2 text-center">Type</th>
                      <th className="border px-4 py-2 text-center">Beds</th>
                      <th className="border px-4 py-2 text-center">Baths</th>
                      <th className="border px-4 py-2 text-center">
                        Living Sqft
                      </th>
                      <th className="border px-4 py-2 text-right">Price</th>
                      <th className="border px-4 py-2 text-left">For</th>
                      <th className="border px-4 py-2 text-left">Agent</th>
                      <th className="border px-4 py-2 text-center">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered?.map((property, index) => (
                      <tr key={index} className="hover:bg-gray-100 group">
                        <td className="border px-4 py-2 text-center font-mono">
                          <Link
                            to={`/property/${property.property_id}`}
                            className="text-blue-600 hover:underline"
                          >
                            {property.property_id || "N/A"}
                          </Link>
                        </td>
                        <td className="border px-4 py-2 text-center">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center text-gray-600 hover:text-blue-600"
                            onClick={() => {
                              const url = property?.media_url?.split(",")[0];
                              if (url) setPreviewImage(cloudinaryUrl(url));
                            }}
                          >
                            <BsCardImage size={18} />
                          </button>
                        </td>
                        <td className="border px-4 py-2">
                          {property.street_number} {property.street_name}
                        </td>
                        <td className="border px-4 py-2">{property.city}</td>
                        <td className="border px-4 py-2 text-center">
                          {property.type || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.beds_total || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.baths_full || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          {property.living_sqft
                            ? property.living_sqft.toLocaleString()
                            : "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-right font-semibold">
                          $
                          {property.price
                            ? property.price.toLocaleString()
                            : "N/A"}
                          {property.for === "Lease" ? "/mo" : ""}
                        </td>
                        <td className="border px-4 py-2">
                          {property.for || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-sm">
                          {property.realtor_name || "N/A"}
                        </td>
                        <td className="border px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/property/${property.property_id}`)
                            }
                            className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewImage && (
                  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 overflow-hidden">
                      <div className="flex justify-between items-center px-4 py-3 border-b">
                        <h3 className="text-lg font-semibold">Image Preview</h3>
                        <button
                          className="text-gray-600 hover:text-black"
                          onClick={() => setPreviewImage(null)}
                        >
                          Close
                        </button>
                      </div>
                      <div className="bg-black">
                        <img
                          src={previewImage}
                          alt="Property"
                          className="w-full max-h-[70vh] object-contain"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Expired;
